import { useEffect, useState, useContext, useCallback } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { SocketContext } from '../context/SocketContext';
import { useHideMenu } from '../hooks/useHideMenu';
import LoadingOverlay from '../components/LoadingOverlay';
import logoParaTicket from '../assets/femcc-01/logoParaTicket.png';
import logo from '../assets/logo.png';
import { serverApi } from '../api';

/* ─────────────────────────────────────────────
   CONSTANTES
───────────────────────────────────────────── */
const tiposDocumentoLabels = {
    CC: 'Cédula de Ciudadanía',
    TI: 'Tarjeta de Identidad',
    RC: 'Registro Civil',
    CE: 'Cédula de Extranjería',
    PE: 'Permiso Especial de Permanencia',
    PT: 'Permiso por Protección Temporal',
};

const MySwal = withReactContent(Swal);

const sweet = (title, html) => {
    MySwal.fire({ title, html, showConfirmButton: false, timer: 10000 });
};

const iconSvgOk = `
  <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="28" fill="#22C55E"/>
    <path d="M18 32l10 10 18-18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: SelectorTipoDoc
───────────────────────────────────────────── */
const SelectorTipoDoc = ({ value, onChange }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        {Object.entries(tiposDocumentoLabels).map(([key, label]) => {
            const active = value === key;
            return (
                <button
                    key={key}
                    type="button"
                    onClick={() => onChange(key)}
                    style={{
                        padding: '14px 6px',
                        borderRadius: 14,
                        border: active ? '3px solid #0d7367' : '2px solid #dde1ea',
                        background: active
                            ? 'linear-gradient(135deg,#0d7367,#14b8a6)'
                            : '#f1f5f9',
                        color: active ? '#fff' : '#475569',
                        fontWeight: active ? 800 : 600,
                        fontSize: 13,
                        lineHeight: 1.2,
                        cursor: 'pointer',
                        transition: 'all 0.18s ease',
                        boxShadow: active
                            ? '0 4px 16px rgba(13,115,103,0.35)'
                            : '0 1px 3px rgba(0,0,0,0.08)',
                        textAlign: 'center',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        touchAction: 'manipulation',
                    }}
                >
                    <span style={{ fontSize: 15, fontWeight: 900 }}>{key}</span>
                    <br />
                    <span style={{ fontSize: 10, opacity: 0.85 }}>{label.split(' ')[0]}</span>
                </button>
            );
        })}
    </div>
);

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: TecladoNumerico
───────────────────────────────────────────── */
const NUMERIC_ROWS = [[1, 2, 3], [4, 5, 6], [7, 8, 9], [null, 0, 'DEL']];

const TecladoNumerico = ({ onKey }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {NUMERIC_ROWS.flat().map((key, idx) => {
            const isDel = key === 'DEL';
            const isEmpty = key === null;
            return (
                <button
                    key={idx}
                    type="button"
                    disabled={isEmpty}
                    onClick={() => !isEmpty && onKey(key)}
                    style={{
                        height: 72,
                        borderRadius: 14,
                        border: 'none',
                        background: isEmpty
                            ? 'transparent'
                            : isDel
                            ? '#fee2e2'
                            : 'white',
                        color: isDel ? '#dc2626' : '#0f172a',
                        fontSize: isDel ? 18 : 26,
                        fontWeight: 700,
                        cursor: isEmpty ? 'default' : 'pointer',
                        boxShadow: isEmpty ? 'none' : '0 2px 8px rgba(0,0,0,0.10)',
                        transition: 'transform 0.1s, box-shadow 0.1s',
                        touchAction: 'manipulation',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onTouchStart={e => {
                        if (!isEmpty) e.currentTarget.style.transform = 'scale(0.92)';
                    }}
                    onTouchEnd={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                >
                    {isDel ? '⌫' : key}
                </button>
            );
        })}
    </div>
);

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: TecladoQwerty
───────────────────────────────────────────── */
const QWERTY_ROWS = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ñ'],
    ['⇧', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫'],
    ['ESPACIO'],
];

const TecladoQwerty = ({ onKey, onDelete, onSpace, onCaps, capsOn }) => {
    const btnStyle = (key) => {
        const isSpecial = key === '⌫' || key === '⇧' || key === 'ESPACIO';
        const isCaps = key === '⇧';
        return {
            height: key === 'ESPACIO' ? 52 : 46,
            minWidth: key === 'ESPACIO' ? '60%' : 36,
            flex: key === 'ESPACIO' ? '0 0 60%' : '1 1 0',
            borderRadius: 10,
            border: 'none',
            background: isCaps && capsOn
                ? '#0d7367'
                : isSpecial
                ? '#e2e8f0'
                : 'white',
            color: isCaps && capsOn ? '#fff' : isSpecial ? '#475569' : '#0f172a',
            fontSize: key === 'ESPACIO' ? 13 : 16,
            fontWeight: isSpecial ? 700 : 600,
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
            transition: 'transform 0.1s',
            touchAction: 'manipulation',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        };
    };

    const handleKey = (key) => {
        if (key === '⌫') return onDelete();
        if (key === '⇧') return onCaps();
        if (key === 'ESPACIO') return onSpace();
        onKey(capsOn ? key : key.toLowerCase());
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {QWERTY_ROWS.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', justifyContent: 'center', gap: 5, flexWrap: ri === 3 ? 'wrap' : 'nowrap' }}>
                    {row.map((key) => (
                        <button
                            key={key}
                            type="button"
                            style={btnStyle(key)}
                            onClick={() => handleKey(key)}
                            onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
                            onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                            {key === '⇧' ? (capsOn ? '⇑' : '⇧') : key === 'ESPACIO' ? 'ESPACIO' : key}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
};

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: CampoTextoTouch
   Input de solo lectura para uso con teclado en pantalla
───────────────────────────────────────────── */
const CampoTextoTouch = ({ label, value, active, onActivate }) => (
    <div
        style={{
            position: 'relative',
            marginBottom: 12,
            cursor: 'pointer',
        }}
        onClick={onActivate}
        onTouchEnd={onActivate}
    >
        <label style={{
            position: 'absolute', left: 14,
            top: value ? 7 : '50%',
            transform: value ? 'none' : 'translateY(-50%)',
            fontSize: value ? 10 : 14,
            fontWeight: value ? 700 : 400,
            color: active ? '#0d7367' : value ? '#14b8a6' : '#94a3b8',
            transition: 'all 0.2s ease',
            pointerEvents: 'none',
            letterSpacing: value ? 0.8 : 0,
            textTransform: value ? 'uppercase' : 'none',
            zIndex: 1,
        }}>
            {label}
        </label>
        <div style={{
            width: '100%',
            minHeight: 52,
            padding: value ? '22px 14px 8px' : '15px 14px',
            background: active ? '#e6f4f2' : '#f8fafc',
            border: `2px solid ${active ? '#0d7367' : '#e2e8f0'}`,
            borderRadius: 12,
            color: '#0f172a',
            fontSize: 16,
            fontWeight: 600,
            boxShadow: active ? '0 0 0 4px rgba(13,115,103,0.15)' : 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            userSelect: 'none',
            letterSpacing: 0.5,
        }}>
            {value || <span style={{ color: 'transparent' }}>_</span>}
            {active && (
                <span style={{
                    display: 'inline-block',
                    width: 2,
                    height: 18,
                    background: '#0d7367',
                    marginLeft: 2,
                    verticalAlign: 'middle',
                    animation: 'blink 1s step-end infinite',
                }} />
            )}
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: CardTipoAtencion
───────────────────────────────────────────── */
const CardTipoAtencion = ({ item, onClick }) => {
    const [pressed, setPressed] = useState(false);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onTouchStart={() => setPressed(true)}
            onTouchEnd={() => { setPressed(false); onClick(); }}
            onTouchCancel={() => setPressed(false)}
            style={{
                background: pressed ? '#e6f4f2' : 'white',
                border: `2px solid ${pressed ? '#0d7367' : '#e8edf5'}`,
                borderLeft: `6px solid ${item.color || '#0d7367'}`,
                borderRadius: 16,
                padding: '18px 16px',
                marginBottom: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                boxShadow: pressed
                    ? '0 2px 12px rgba(13,115,103,0.18)'
                    : '0 2px 10px rgba(0,0,0,0.07)',
                transform: pressed ? 'scale(0.98)' : 'scale(1)',
                transition: 'all 0.15s ease',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'manipulation',
            }}
        >
            <div style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: `${item.color || '#0d7367'}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                <i className={`${item.icono} fa-lg`} style={{ color: item.color || '#0d7367' }} />
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    {item.tipoAtencion}
                </div>
                {item.complemento && (
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 3, fontWeight: 400 }}>
                        {item.complemento}
                    </div>
                )}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 22 }}>›</div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   COMPONENTE PRINCIPAL
───────────────────────────────────────────── */
export const CrearTicketV2 = () => {
    useHideMenu(true);

    const { conectarSocket } = useContext(SocketContext);

    /* ── Estados globales ── */
    const [entidadStorage] = useState(localStorage.getItem('codigoEntidad') || null);
    const [isLoading, setIsLoading] = useState(false);
    const [tipoAtencion, setTipoAtencion] = useState([]);

    /* ── Datos de pantalla setup (si aún no hay entidad) ── */
    const [setupForm, setSetupForm] = useState({
        codigoEntidad: 'femcc-01',
        codigoUbicacion: '',
    });
    const [ubicaciones, setUbicaciones] = useState([]);

    /* ── Flujo principal ── */
    const [paso, setPaso] = useState(1); // 1=documento | 1b=datos-nuevo | 2=tipo-atencion

    /* ── Paso 1: datos de documento ── */
    const [tipoDoc, setTipoDoc] = useState('CC');
    const [numDoc, setNumDoc] = useState('');

    /* ── Paso 1b: datos nuevo paciente ── */
    const [datosPaciente, setDatosPaciente] = useState({
        nombre1: '', nombre2: '', apellido1: '', apellido2: '',
    });
    const [campoActivo, setCampoActivo] = useState('nombre1');
    const [capsOn, setCapsOn] = useState(true);

    /* ── Paso 2 ── */
    const [pacienteNombre, setPacienteNombre] = useState('');
    const [pacienteEsNuevo, setPacienteEsNuevo] = useState(false);
    const [prioridad, setPrioridad] = useState(false);

    /* ─── Carga tipos de atención al montar ─── */
    useEffect(() => {
        const fetchTipos = async () => {
            try {
                setIsLoading(true);
                const resp = await serverApi.post('/tipos-atencion/lista-para-ticket', {
                    codigoEntidad: localStorage.getItem('codigoEntidad'),
                    codigoUbicacion: localStorage.getItem('codigoUbicacion'),
                });
                if (resp.data.atenciones) setTipoAtencion(resp.data.atenciones);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };
        if (entidadStorage) fetchTipos();
    }, [entidadStorage]);

    /* ─── Setup: buscar ubicaciones ─── */
    const fetchUbicaciones = useCallback(async (codigo) => {
        if (!codigo || codigo.trim().length < 3) return;
        try {
            const resp = await serverApi.post('/auth/select-ubicacion-pantallas', {
                codigoEntidad: codigo,
            });
            const data = (resp.data.ubicaciones || []).map(i => ({
                value: i.codigoUbicacion,
                label: i.nombreUbicacion,
            }));
            setUbicaciones(data);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        if (!entidadStorage && setupForm.codigoEntidad && setupForm.codigoEntidad.trim().length >= 3) {
            fetchUbicaciones(setupForm.codigoEntidad.trim());
        }
    }, [entidadStorage, setupForm.codigoEntidad, fetchUbicaciones]);

    const handleSetupEntidad = (e) => {
        const val = e.target.value;
        // Limpiar sedes anteriores cuando cambia el código de entidad
        setUbicaciones([]);
        setSetupForm(p => ({ ...p, codigoEntidad: val, codigoUbicacion: '' }));
    };

    const handleIngresar = async () => {
        if (!setupForm.codigoEntidad || !setupForm.codigoUbicacion)
            return sweet('Atención', 'Faltan datos de entidad o sede');
        await serverApi.post('/auth/registro-pantallas', setupForm);
        localStorage.setItem('codigoEntidad', setupForm.codigoEntidad);
        localStorage.setItem('codigoUbicacion', setupForm.codigoUbicacion);
        conectarSocket({
            query: {
                codigoEntidad: setupForm.codigoEntidad,
                ubicacion: setupForm.codigoUbicacion,
            },
        });
        window.location.reload();
    };

    /* ─── TECLADO NUMÉRICO ─── */
    const handleNumKey = (key) => {
        if (key === 'DEL') {
            setNumDoc(p => p.slice(0, -1));
        } else {
            setNumDoc(p => p.length >= 12 ? p : p + String(key));
        }
    };

    /* ─── TECLADO QWERTY ─── */
    const handleQwertyKey = useCallback((char) => {
        setDatosPaciente(p => ({ ...p, [campoActivo]: p[campoActivo] + char }));
    }, [campoActivo]);

    const handleQwertyDelete = useCallback(() => {
        setDatosPaciente(p => ({ ...p, [campoActivo]: p[campoActivo].slice(0, -1) }));
    }, [campoActivo]);

    const handleQwertySpace = useCallback(() => {
        setDatosPaciente(p => ({ ...p, [campoActivo]: p[campoActivo] + ' ' }));
    }, [campoActivo]);

    /* ─── PASO 1 → Buscar paciente ─── */
    const buscarPaciente = async () => {
        if (!tipoDoc || !numDoc) return sweet('Atención', 'Complete el tipo y número de documento');

        try {
            setIsLoading(true);
            const resp = await serverApi.post('/tickets/busca-paciente', {
                codigoEntidad: localStorage.getItem('codigoEntidad'),
                numeroDocumento: numDoc,
            });

            const lista = resp.data.paciente || [];

            if (lista.length === 0) {
                // No encontrado → ir a captura de datos
                setPacienteNombre('');
                setPacienteEsNuevo(true);
                setDatosPaciente({ nombre1: '', nombre2: '', apellido1: '', apellido2: '' });
                setCampoActivo('nombre1');
                setCapsOn(true);
                setPaso('1b');
            } else {
                setPacienteNombre(lista[0].paciente || '');
                setPacienteEsNuevo(false);
                setPaso(2);
            }
        } catch (e) {
            console.error(e);
            sweet('Error', 'No se pudo consultar el paciente');
        } finally {
            setIsLoading(false);
        }
    };

    /* ─── PASO 1b → Confirmar datos nuevos ─── */
    const confirmarDatosNuevo = () => {
        const { nombre1, apellido1 } = datosPaciente;
        if (!nombre1.trim() || !apellido1.trim())
            return sweet('Atención', 'El primer nombre y primer apellido son obligatorios');

        const nombreCompleto = [datosPaciente.nombre1, datosPaciente.nombre2, datosPaciente.apellido1, datosPaciente.apellido2]
            .filter(Boolean).join(' ').toUpperCase();
        setPacienteNombre(nombreCompleto);
        setPaso(2);
    };

    /* ─── PASO 2 → Crear ticket ─── */
    const crearTicket = async (codigoTipoAtencion, uidTipoAtencion) => {
        const codigoEntidad = localStorage.getItem('codigoEntidad');
        const codigoUbicacion = localStorage.getItem('codigoUbicacion');

        try {
            setIsLoading(true);

            // Si es nuevo, registrar cliente primero
            if (pacienteEsNuevo) {
                await serverApi.post('/tickets/registra-cliente', {
                    codigoEntidad,
                    tipoDocumento: tipoDoc,
                    numeroDocumento: numDoc,
                    ...datosPaciente,
                });
            }

            const resp = await serverApi.post('/tickets/crear-ticket', {
                codigoEntidad,
                codigoUbicacion,
                uidTipoAtencion,
                indPrioridad: prioridad ? 1 : 0,
                tipoDocumento: tipoDoc,
                numeroDocumento: numDoc,
            });

            const result = resp.data.ticket;

            sweet('', `
                <div style="display:flex;flex-direction:column;align-items:center;padding:8px">
                    ${iconSvgOk}
                    <br/>
                    <h2 style="margin:8px 0;font-weight:900">Turno: ${codigoTipoAtencion}-${result.numero}</h2>
                    <p style="color:#475569">Por favor, esté atento al televisor o al llamado de voz para saber en qué módulo será atendido.</p>
                    <b style="font-size:18px;margin-top:8px">¡Gracias!</b>
                </div>
            `);

            // Imprimir ticket
            const win = window.open('', 'PRINT', 'height=400,width=300');
            win.document.write(`
                <html><head><title>Ticket</title>
                <style>
                    body{font-family:Arial;text-align:center;width:58mm;margin:0;padding:10px;}
                    h1,h4,h5,h6{margin:4px 0;}img{width:120px;}
                </style></head>
                <body>
                    <img src="${logoParaTicket}"/>
                    <h1>Turno: ${codigoTipoAtencion}-${result.numero}</h1>
                    <h4>Paciente: ${numDoc}</h4>
                    ${pacienteNombre ? `<h5>${pacienteNombre}</h5>` : ''}
                    <h5>${result.fecha}</h5>
                    <h6>-- PITSoluciones.com --</h6>
                </body></html>
            `);
            win.document.close();
            win.focus();
            win.print();
            setTimeout(() => win.close(), 4000);

            // Reset
            setNumDoc('');
            setTipoDoc('CC');
            setPrioridad(false);
            setPacienteNombre('');
            setPacienteEsNuevo(false);
            setPaso(1);

        } catch (e) {
            console.error(e);
            sweet('Error', 'No se pudo crear el ticket');
        } finally {
            setIsLoading(false);
        }
    };

    /* ═══════════════════════════════════════════
       RENDER: Setup (sin entidad configurada)
    ═══════════════════════════════════════════ */
    if (!entidadStorage) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#f4f7f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: 24,
                    padding: '36px 32px',
                    width: '100%',
                    maxWidth: 420,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        <img src={logo} alt="logo" style={{ height: 64, marginBottom: 16 }} />
                        <h3 style={{ color: '#0d7367', fontWeight: 900, margin: 0 }}>Configurar Kiosko</h3>
                        <p style={{ color: '#64748b', marginTop: 6 }}>Ingrese su entidad y sede de atención</p>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase' }}>
                            Código Entidad
                        </label>
                        <input
                            type="text"
                            value={setupForm.codigoEntidad}
                            onChange={handleSetupEntidad}
                            placeholder="Ej: femcc-01"
                            style={{
                                width: '100%', padding: '12px 14px', borderRadius: 12,
                                border: '2px solid #e2e8f0', fontSize: 16, marginTop: 6,
                                outline: 'none', boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    {ubicaciones.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase' }}>
                                Sede / Ubicación
                            </label>
                            <select
                                value={setupForm.codigoUbicacion}
                                onChange={e => setSetupForm(p => ({ ...p, codigoUbicacion: e.target.value }))}
                                style={{
                                    width: '100%', padding: '12px 14px', borderRadius: 12,
                                    border: '2px solid #e2e8f0', fontSize: 16, marginTop: 6,
                                    boxSizing: 'border-box', background: 'white', cursor: 'pointer',
                                }}
                            >
                                <option value="">Seleccione...</option>
                                {ubicaciones.map(u => (
                                    <option key={u.value} value={u.value}>{u.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {setupForm.codigoUbicacion && (
                        <button
                            onClick={handleIngresar}
                            style={{
                                width: '100%', padding: '14px', borderRadius: 14,
                                border: 'none', background: 'linear-gradient(135deg, #0d7367, #14b8a6)',
                                color: '#fff', fontSize: 16, fontWeight: 800,
                                cursor: 'pointer', boxShadow: '0 4px 16px rgba(13,115,103,0.4)',
                            }}
                        >
                            INGRESAR AL KIOSKO →
                        </button>
                    )}
                </div>
            </div>
        );
    }

    /* ═══════════════════════════════════════════
       ESTILOS GLOBALES (inyectados una vez)
    ═══════════════════════════════════════════ */
    const globalCss = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes slideUp {
            from { opacity:0; transform: translateY(24px); }
            to   { opacity:1; transform: translateY(0); }
        }
        .ct2-card { animation: slideUp 0.3s ease; }
        .ct2-root * { font-family: 'Inter', Arial, sans-serif; }
    `;

    /* ─── Layout vars ─── */
    const bg = '#f4f7f6';

    const panelStyle = {
        background: 'white',
        borderRadius: 22,
        padding: '24px 20px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
        width: '100%',
        boxSizing: 'border-box',
    };

    const headerLabelStyle = {
        fontSize: 11,
        fontWeight: 700,
        color: '#0d7367',
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        marginBottom: 4,
    };

    return (
        <>
            <style>{globalCss}</style>
            {isLoading && <LoadingOverlay />}

            <div
                className="ct2-root"
                style={{
                    minHeight: '100dvh',
                    background: bg,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '16px 12px 24px',
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                }}
            >
                {/* ── LOGO ── */}
                <img
                    src={logo}
                    alt="logo"
                    style={{
                        height: 52,
                        marginBottom: 16,
                        opacity: 0.92,
                    }}
                />

                {/* ─────────────────────────────────────────
                    PASO 1: Ingreso de documento
                ───────────────────────────────────────── */}
                {paso === 1 && (
                    <div className="ct2-card" style={{ width: '100%', maxWidth: 580 }}>

                        {/* Display número */}
                        <div style={{
                            background: 'linear-gradient(135deg, #0d7367 0%, #0c695e 100%)',
                            borderRadius: 18,
                            padding: '14px 20px',
                            marginBottom: 14,
                            textAlign: 'center',
                            boxShadow: '0 8px 24px rgba(13,115,103,0.25)',
                        }}>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 }}>
                                Número de Documento
                            </div>
                            <div style={{
                                fontSize: numDoc ? 36 : 24,
                                fontWeight: 900,
                                color: numDoc ? 'white' : 'rgba(255,255,255,0.4)',
                                letterSpacing: 4,
                                minHeight: 48,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                {numDoc || '···'}
                                {numDoc && (
                                    <span style={{
                                        display: 'inline-block', width: 3, height: 38,
                                        background: '#14b8a6', marginLeft: 6, borderRadius: 2,
                                        animation: 'blink 1s step-end infinite',
                                    }} />
                                )}
                            </div>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 12,
                            alignItems: 'start',
                        }}>
                            {/* Selector tipo doc + botón */}
                            <div style={panelStyle}>
                                <div style={headerLabelStyle}>Tipo de Documento</div>
                                <SelectorTipoDoc value={tipoDoc} onChange={setTipoDoc} />

                                <button
                                    onClick={buscarPaciente}
                                    disabled={!numDoc || numDoc.length < 4}
                                    style={{
                                        width: '100%',
                                        padding: '16px',
                                        borderRadius: 14,
                                        border: 'none',
                                        background: (!numDoc || numDoc.length < 4)
                                            ? '#e2e8f0'
                                            : 'linear-gradient(135deg,#16a34a,#22c55e)',
                                        color: (!numDoc || numDoc.length < 4) ? '#94a3b8' : 'white',
                                        fontSize: 16,
                                        fontWeight: 800,
                                        cursor: (!numDoc || numDoc.length < 4) ? 'not-allowed' : 'pointer',
                                        boxShadow: (!numDoc || numDoc.length < 4)
                                            ? 'none'
                                            : '0 4px 16px rgba(22,163,74,0.4)',
                                        transition: 'all 0.2s ease',
                                        letterSpacing: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 10,
                                        touchAction: 'manipulation',
                                    }}
                                >
                                    <span style={{ fontSize: 20 }}>→</span> CONTINUAR
                                </button>
                            </div>

                            {/* Teclado numérico */}
                            <div style={panelStyle}>
                                <div style={headerLabelStyle}>Teclado</div>
                                <TecladoNumerico onKey={handleNumKey} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ─────────────────────────────────────────
                    PASO 1b: Datos del nuevo paciente
                ───────────────────────────────────────── */}
                {paso === '1b' && (
                    <div className="ct2-card" style={{ width: '100%', maxWidth: 680 }}>

                        {/* Barra info */}
                        <div style={{
                            background: '#fffbeb',
                            border: '1.5px solid #fcd34d',
                            borderRadius: 14,
                            padding: '12px 16px',
                            marginBottom: 14,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                        }}>
                            <span style={{ fontSize: 24 }}>⚠️</span>
                            <div>
                                <div style={{ color: '#92400e', fontWeight: 800, fontSize: 14 }}>
                                    Paciente no encontrado
                                </div>
                                <div style={{ color: '#b45309', fontSize: 12 }}>
                                    Doc: <b>{tipoDoc}</b> — <b>{numDoc}</b> · Por favor ingrese sus datos
                                </div>
                            </div>
                        </div>

                        {/* Panel superior: campos + botones */}
                        <div style={{ ...panelStyle, marginBottom: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                                <CampoTextoTouch
                                    label="Primer Nombre *"
                                    value={datosPaciente.nombre1}
                                    active={campoActivo === 'nombre1'}
                                    onActivate={() => setCampoActivo('nombre1')}
                                />
                                <CampoTextoTouch
                                    label="Segundo Nombre"
                                    value={datosPaciente.nombre2}
                                    active={campoActivo === 'nombre2'}
                                    onActivate={() => setCampoActivo('nombre2')}
                                />
                                <CampoTextoTouch
                                    label="Primer Apellido *"
                                    value={datosPaciente.apellido1}
                                    active={campoActivo === 'apellido1'}
                                    onActivate={() => setCampoActivo('apellido1')}
                                />
                                <CampoTextoTouch
                                    label="Segundo Apellido"
                                    value={datosPaciente.apellido2}
                                    active={campoActivo === 'apellido2'}
                                    onActivate={() => setCampoActivo('apellido2')}
                                />
                            </div>

                            {/* Tabs de campos */}
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, marginTop: 4 }}>
                                {[
                                    { key: 'nombre1', label: '1er Nombre' },
                                    { key: 'nombre2', label: '2do Nombre' },
                                    { key: 'apellido1', label: '1er Apellido' },
                                    { key: 'apellido2', label: '2do Apellido' },
                                ].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setCampoActivo(f.key)}
                                        style={{
                                            padding: '6px 14px',
                                            borderRadius: 20,
                                            border: 'none',
                                            background: campoActivo === f.key ? '#0d7367' : '#e2e8f0',
                                            color: campoActivo === f.key ? 'white' : '#475569',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            touchAction: 'manipulation',
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Teclado QWERTY */}
                        <div style={{ ...panelStyle, marginBottom: 12 }}>
                            <TecladoQwerty
                                onKey={handleQwertyKey}
                                onDelete={handleQwertyDelete}
                                onSpace={handleQwertySpace}
                                onCaps={() => setCapsOn(p => !p)}
                                capsOn={capsOn}
                            />
                        </div>

                        {/* Botones acción */}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button
                                onClick={() => { setPaso(1); }}
                                style={{
                                    flex: 1, padding: '15px', borderRadius: 14,
                                    border: '2px solid #cbd5e1',
                                    background: '#ffffff',
                                    color: '#475569', fontSize: 15, fontWeight: 700,
                                    cursor: 'pointer', touchAction: 'manipulation',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                }}
                            >
                                ← Atrás
                            </button>
                            <button
                                onClick={confirmarDatosNuevo}
                                style={{
                                    flex: 2, padding: '15px', borderRadius: 14,
                                    border: 'none',
                                    background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                                    color: 'white', fontSize: 15, fontWeight: 800,
                                    cursor: 'pointer', touchAction: 'manipulation',
                                    boxShadow: '0 4px 16px rgba(22,163,74,0.4)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                }}
                            >
                                ✓ Confirmar Datos
                            </button>
                        </div>
                    </div>
                )}

                {/* ─────────────────────────────────────────
                    PASO 2: Selección de tipo de atención
                ───────────────────────────────────────── */}
                {paso === 2 && (
                    <div className="ct2-card" style={{ width: '100%', maxWidth: 620 }}>

                        {/* Info paciente */}
                        <div style={{
                            background: '#ffffff',
                            borderRadius: 16,
                            padding: '16px 20px',
                            marginBottom: 14,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            flexWrap: 'wrap',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{
                                    width: 48, height: 48, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #0d7367, #14b8a6)',
                                    color: 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 22, flexShrink: 0,
                                }}>
                                    👤
                                </div>
                                <div>
                                    <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
                                        {tiposDocumentoLabels[tipoDoc]} · {numDoc}
                                        {pacienteEsNuevo && <span style={{
                                            background: '#f59e0b', color: 'white',
                                            fontSize: 10, fontWeight: 800, borderRadius: 6,
                                            padding: '2px 6px', marginLeft: 8,
                                        }}>NUEVO</span>}
                                    </div>
                                    <div style={{ color: '#0f172a', fontSize: 18, fontWeight: 800, marginTop: 2 }}>
                                        {pacienteNombre || 'Paciente'}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setPaso(1)}
                                style={{
                                    background: '#f1f5f9', border: '1px solid #cbd5e1',
                                    color: '#0d7367', borderRadius: 10, padding: '8px 14px',
                                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                    touchAction: 'manipulation',
                                }}
                            >
                                ← Cambiar
                            </button>
                        </div>

                        {/* Toggle prioridad */}
                        <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={() => setPrioridad(p => !p)}
                                style={{
                                    padding: '14px 28px',
                                    borderRadius: 50,
                                    border: prioridad ? 'none' : '2.5px solid #ef4444',
                                    background: prioridad
                                        ? 'linear-gradient(135deg,#dc2626,#ef4444)'
                                        : '#ffffff',
                                    color: prioridad ? 'white' : '#ef4444',
                                    fontSize: 16,
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                    touchAction: 'manipulation',
                                    boxShadow: prioridad ? '0 4px 20px rgba(220,38,38,0.45)' : '0 2px 8px rgba(0,0,0,0.06)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex', alignItems: 'center', gap: 10,
                                }}
                            >
                                <i className="fa-solid fa-wheelchair" style={{ fontSize: 18 }} />
                                {prioridad ? '✓ ATENCIÓN PRIORITARIA' : 'ATENCIÓN PRIORITARIA'}
                            </button>
                        </div>

                        {/* Tipos de atención */}
                        <div style={{ ...panelStyle }}>
                            <div style={headerLabelStyle}>¿Cuál es el motivo de su visita?</div>
                            <div style={{ marginTop: 12 }}>
                                {tipoAtencion.length === 0 && (
                                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>
                                        Sin tipos de atención configurados
                                    </div>
                                )}
                                {tipoAtencion.map(item => (
                                    <CardTipoAtencion
                                        key={item.uidTipoAtencion}
                                        item={item}
                                        onClick={() => crearTicket(item.codigoTipoAtencion, item.uidTipoAtencion)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
